import styled from '@emotion/styled';

import { useTranslation } from 'react-i18next';
import { epochDate } from '@hotjar/webapp/lib/time';
import { tokens, Tooltip, Icon, PlanBadge, Link } from '@hotjar/hotsauce';
import { abbrNumber } from '@hotjar/insights-common/lib/number';
import { capitalize } from '@hotjar/insights-common/lib/string';
import { useSitePlans } from '@hotjar/webapp/hooks/useSitePlans';
import type { OwnedAccount } from '@hotjar/account';
import type { SiteWithPriceInfo } from '@hotjar/webapp/domains/revenue/site/types';
import {
  isPlanChangePeriod,
  isPlanDowngradePeriod,
  isPlanLocked,
  mapSiteDtoToModel,
} from '@hotjar/site';
import type { Plan } from '@hotjar/webapp/domains/revenue/plan/types';
import { mapOwnedAccountModelToDto } from '@hotjar/account';
import { useHelpHotjarUrl } from '@hotjar/translations';
import { PlanSelectDropdown } from '../PlanSelectDropdown';
import { SiteLockedIcon } from './SiteLockedIcon';
import { DetailLabel } from './components/DetailLabel';
import { DetailLabelIcon } from './components/DetailLabelIcon';

interface Props {
  account: OwnedAccount;
  site: SiteWithPriceInfo;
  onPlanChange: (organizationId: number, siteId: number, plan: Plan) => void;
  onCancelDowngrade?: (site: SiteWithPriceInfo) => void;
  planId: string;
  unlimitedSampleRate: boolean;
}

const DataCollectionRateContainer = styled.div`
  float: left;
  margin-right: 5%;
  position: relative;
  width: 54%;
`;

const DowngradeMessageContainer = styled.div`
  color: ${tokens.color.text_light};
  font-size: 12px;
  display: flex;
`;

const ArrowDownIcon = styled(Icon)`
  margin: 0 ${tokens.spacer.x1};
`;

const SiteLockedContainer = styled.div`
  padding-top: 10px;
`;

const PlanBadgeAlignedToRight = styled(PlanBadge)`
  margin-right: ${tokens.spacer.x1};
`;

export const SiteDetailsCollectionRate = ({
  account,
  site,
  onPlanChange,
  onCancelDowngrade,
  planId,
  unlimitedSampleRate,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const { plan, effectivePlan } = useSitePlans(site);

  const siteModel = mapSiteDtoToModel(site);
  const planDowngradePeriod = isPlanDowngradePeriod(siteModel);
  const planChangePeriod = isPlanChangePeriod(siteModel);
  const siteLocked = isPlanLocked(siteModel, account);

  return (
    <DataCollectionRateContainer>
      <Tooltip text={t('billing:site-box.sampling-tooltip')}>
        <a
          target="_blank"
          href={`${helpHotjarUrl}/articles/360012620453`}
          rel="noopener noreferrer"
        >
          <DetailLabel>{t('billing:site-box.plan')}</DetailLabel>
          <DetailLabelIcon icon="info_alt" />
        </a>
      </Tooltip>

      {!siteLocked && !planDowngradePeriod && (
        <PlanSelectDropdown
          account={mapOwnedAccountModelToDto(account)}
          activePlanId={planId}
          onChange={(selectedPlan: Plan) =>
            onPlanChange(site.organization_id, site.id, selectedPlan)
          }
        />
      )}

      {/* FAIR DOWNGRADES: Sample rate only. */}
      {planDowngradePeriod && site.plan_change_date && (
        <DowngradeMessageContainer>
          <ArrowDownIcon size="l" color="text_error" icon="arrow_down" />
          <span>
            {effectivePlan.featureGroup === plan.featureGroup && (
              <span data-testid="fair-downgrade-same-featuregroup">
                {t('billing:site-box.downgrade-from', {
                  downgradeInitial: abbrNumber(
                    effectivePlan.ccDailyAllowance || effectivePlan.sampleRate,
                  ),
                })}{' '}
                &rarr;{' '}
                {t('billing:site-box.downgrade-to', {
                  downgradeFinal: abbrNumber(plan.ccDailyAllowance || plan.sampleRate),
                  sessionOrPageView: plan.ccDailyAllowance
                    ? t('billing:site-box.daily-sessions')
                    : t('billing:site-box.daily-pageviews'),
                  planChangeDate: epochDate(site.plan_change_date),
                })}
              </span>
            )}

            {effectivePlan.featureGroup !== plan.featureGroup && (
              <span data-testid="fair-downgrade-different-featuregroup">
                {t('billing:site-box.downgrade-from', {
                  downgradeInitial: capitalize(effectivePlan.featureGroup),
                })}{' '}
                &rarr;{' '}
                {t('billing:site-box.downgrade-to', {
                  downgradeFinal: capitalize(plan.featureGroup),
                  planChangeDate: epochDate(site.plan_change_date),
                })}
              </span>
            )}

            {!siteLocked && onCancelDowngrade && (
              /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
              <Link
                as="button"
                kind="standalone"
                onClick={() => onCancelDowngrade(site)}
                data-testid="cancel-downgrade"
              >
                {t('billing:site-box.cancel-downgrade')}
              </Link>
            )}
          </span>
        </DowngradeMessageContainer>
      )}

      {/* SITE LOCKED: Disabled with lock icon. */}
      {siteLocked && !planChangePeriod && (
        <SiteLockedContainer data-testid="site-locked">
          <PlanBadgeAlignedToRight kind={plan.featureGroup} size="m" />
          {t('billing:site-box.page-view-rate', {
            sampleRate: unlimitedSampleRate
              ? t('billing:site-box.unlimited')
              : abbrNumber(site.sample_rate),
          })}
          <SiteLockedIcon />
        </SiteLockedContainer>
      )}
    </DataCollectionRateContainer>
  );
};
